<template>
    <div
        class="p-4 bg-white border-t border-gray-200"
        :class="{
            'opacity-50 cursor-progress': isProcessing,
            'opacity-50 cursor-not-allowed pointer-events-none': disabled,
        }"
    >
        <div class="flex flex-col gap-y-[8px]" :class="{ 'pointer-events-none': isProcessing }">
            <div
                class="flex flex-wrap items-start gap-3 mb-[6px]"
                v-if="messageAttachments.length > 0"
            >
                <sms-draft-attachment-preview
                    :attachment="attachment"
                    :preview-label="t('smsCommunication.attachmentPreview')"
                    :remove-label="t('smsCommunication.removeAttachment')"
                    :key="getAttachmentKey(attachment.file)"
                    @remove="removeAttachment(index)"
                    v-for="(attachment, index) in messageAttachments"
                />
            </div>

            <div
                class="relative flex w-full flex-col rounded-xl border-2 border-gray-300"
                data-testid="sms-message-input-box"
            >
                <textarea
                    rows="1"
                    class="w-full bg-transparent px-4 py-3 resize-none focus:outline-none overflow-y-auto"
                    :aria-describedby="hasReachedSmsTextLimit ? 'sms-text-limit-error' : undefined"
                    :placeholder="t('smsCommunication.smsInputPlaceholder')"
                    :disabled="disabled"
                    data-testid="message-textarea"
                    ref="textarea"
                    @compositionstart="onCompositionStart"
                    @compositionend="onCompositionEnd"
                    @keydown.enter="onEnterKey"
                    @input="onMessageTextInput"
                    v-model="messageText"
                    v-show="smsAiDraft.state.value !== SmsAiDraftState.Loading"
                />

                <div
                    id="sms-text-limit-error"
                    role="alert"
                    class="px-4 pb-1 text-xs text-red-600"
                    data-testid="sms-text-limit-error"
                    v-if="hasReachedSmsTextLimit"
                >
                    {{
                        t('smsCommunication.aiDraft.smsTextLimitReached', {
                            limit: SMS_TEXT_MAX_LENGTH,
                        })
                    }}
                </div>

                <sms-ai-draft-loading-overlay
                    class="mb-2"
                    @stop="stopSmsAiDraft"
                    v-if="smsAiDraft.state.value === SmsAiDraftState.Loading"
                />

                <ai-draft-feedback-controls
                    class="px-4 pb-2"
                    :feedback="smsAiDraft.feedback.value"
                    i18n-namespace="smsCommunication.aiDraft"
                    testid-prefix="sms-ai"
                    @select-feedback="smsAiDraft.selectFeedback"
                    @feedback-submit="smsAiDraft.submitFeedback"
                    @panel-resize="emitComposerResize"
                    v-if="smsAiDraft.showFeedbackControls.value && hasMessageText"
                />

                <sms-ai-draft-controls
                    class="px-4 pb-2"
                    :can-generate="canGenerateSmsAiDraft"
                    :can-undo="smsAiDraft.canUndo.value"
                    :can-update="canUpdateSmsAiDraft"
                    :disabled="areComposerActionsDisabled"
                    :has-generated-draft="smsAiDraft.hasGeneratedDraft.value && hasMessageText"
                    :has-text="hasMessageText"
                    :instruction-reset-key="smsAiInstructionResetKey"
                    :rate-limit-seconds="smsAiDraft.rateLimitSeconds.value"
                    :service-lead-disabled="smsAiDraftEntitlement.serviceLeadDisabled.value"
                    :state="smsAiDraft.state.value"
                    @generate="generateSmsAiDraft"
                    @panel-resize="emitComposerResize"
                    @undo="undoSmsAiDraft"
                    @update="updateSmsAiDraft"
                    v-if="smsAiDraftEntitlement.showEntryPoint.value"
                />

                <div class="flex items-center justify-between px-2 pb-2">
                    <div class="flex items-center">
                        <button
                            class="p-2 text-gray-600 hover:text-blue-500"
                            :disabled="areComposerActionsDisabled"
                            data-testid="attachment-button"
                            @click="selectImage"
                        >
                            <icon name="regular/attachment" />
                        </button>

                        <input
                            class="hidden"
                            type="file"
                            multiple
                            accept="image/gif,image/jpeg,image/png,.pdf,application/pdf"
                            data-testid="attachment-input"
                            ref="fileInput"
                            @change="onFileChange"
                        />

                        <button
                            class="p-2 text-gray-600 hover:text-blue-500"
                            :disabled="areComposerActionsDisabled"
                            data-testid="template-button"
                            @click="openSmsTemplateModal"
                        >
                            <activix-spinner class="h-4 w-4" v-if="isParsingTemplate" />
                            <icon name="regular/common-file-text" v-else />
                        </button>

                        <div class="relative" v-click-away="closeEmojiPicker">
                            <button
                                class="p-2 text-gray-600 hover:text-blue-500"
                                :disabled="areComposerActionsDisabled"
                                data-testid="emoji-button"
                                @click.stop="toggleEmojiPicker"
                            >
                                <icon name="regular/smiley-smile-2" />
                            </button>

                            <div
                                class="absolute bottom-full left-0 z-10 mb-2"
                                v-if="isEmojiPickerOpen"
                            >
                                <emoji-picker :native="true" @select="onEmojiSelect" />
                            </div>
                        </div>
                    </div>

                    <span class="flex shrink-0">
                        <button
                            class="transition-colors duration-150"
                            :class="sendSmsButtonStyleClassName"
                            :disabled="isSendUnavailable"
                            data-testid="send-button"
                            type="button"
                            @click="sendMessage"
                        >
                            <activix-spinner class="h-4 w-4" v-if="isSending" />
                            <send-sms-icon
                                class="h-6 w-6 pointer-events-none"
                                data-testid="send-icon"
                                v-else
                            />
                        </button>
                    </span>
                </div>
            </div>

            <sms-ai-draft-disclaimer v-if="smsAiDraftEntitlement.showEntryPoint.value" />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, nextTick, watch, onBeforeUnmount, toRef } from 'vue';
    import Vapor from 'laravel-vapor';
    import EmojiPicker from 'vue3-emoji-picker';
    import 'vue3-emoji-picker/css';
    import { directive as vClickAway } from 'vue3-click-away';
    import axios from '@/plugins/axios.js';
    import useDynamicModal from '@/store/hooks/useDynamicModal.ts';
    import { useNiotextStore } from '@/store/modules/niotext/store.ts';
    import { useI18n } from '@/hooks/useI18n.ts';
    import { useNotify } from '@/hooks/useNotify.ts';
    import useObjectUrl from '@/hooks/useObjectUrl.ts';
    import { Attachment, ATTACHMENT_MIME_TYPES } from '@/entities/types/ConversationType.ts';
    import ActivixSpinner from '@/components/activix-ui/ActivixSpinner/ActivixSpinner.vue';
    import SmsTemplateModal from '@/components/modals/SmsTemplateModal.vue';
    import SmsDraftAttachmentPreview from '@/components/sms/SmsDraftAttachmentPreview.vue';
    import SmsAiDraftControls from '@/components/sms/aiDraft/SmsAiDraftControls.vue';
    import SmsAiDraftDisclaimer from '@/components/sms/aiDraft/SmsAiDraftDisclaimer.vue';
    import SmsAiDraftLoadingOverlay from '@/components/sms/aiDraft/SmsAiDraftLoadingOverlay.vue';
    import AiDraftFeedbackControls from '@/components/aiDraft/feedback/AiDraftFeedbackControls.vue';
    import { useSmsAiDraft, SmsAiDraftState } from '@/components/sms/aiDraft/useSmsAiDraft.ts';
    import { createSmsAiDraftApiAdapter } from '@/components/sms/aiDraft/smsAiDraftApiAdapter.ts';
    import { useSmsAiDraftEntitlement } from '@/components/sms/aiDraft/useSmsAiDraftEntitlement.ts';
    import { SendSmsIcon } from '@/assets/icons/index.ts';
    import { useTextareaAutoGrow } from '@/components/sms/hooks/useTextareaAutoGrow.ts';
    import { useImeCompositionGuard } from '@/components/sms/hooks/useImeCompositionGuard.ts';

    interface Props {
        disabled?: boolean;
        hasAiDraft?: boolean;
        isServiceLead?: boolean;
    }

    type SmsTemplateLocale = 'en' | 'fr';

    const props = withDefaults(defineProps<Props>(), {
        disabled: false,
        hasAiDraft: false,
        isServiceLead: false,
    });

    const emit = defineEmits<{
        (e: 'write-intent'): void;
        (e: 'send-success'): void;
        (e: 'before-resize'): void;
        (e: 'resize'): void;
    }>();

    const { t } = useI18n();
    const { notify } = useNotify();
    const { openModal } = useDynamicModal();
    const { createObjectUrl, revokeObjectUrl, revokeAllObjectUrls } = useObjectUrl();

    const niotextStore = useNiotextStore();
    const SMS_TEXT_MAX_LENGTH = 1600;
    // Count code points (not UTF-16 units) to match server-side mb_strlen.
    const smsTextLength = (text: string): number => [...text].length;
    const SUPPORTED_ATTACHMENT_MIME_TYPES: readonly string[] = Object.values(ATTACHMENT_MIME_TYPES);

    const smsAiDraft = useSmsAiDraft({
        adapter: createSmsAiDraftApiAdapter(() => niotextStore.currentLeadId),
    });
    const smsAiDraftEntitlement = useSmsAiDraftEntitlement({
        entitled: toRef(props, 'hasAiDraft'),
        serviceLead: toRef(props, 'isServiceLead'),
        leadLocale: computed<string | undefined>(() => niotextStore.currentLead?.locale),
    });

    const messageAttachments = ref<Attachment[]>([]);
    const messageText = ref('');
    const smsAiInstructionResetKey = ref(0);
    const isEmojiPickerOpen = ref(false);
    const isParsingTemplate = ref(false);
    const isSending = ref(false);
    const hasSentWithPendingAttachments = ref(false);
    const composerContextVersion = ref(0);
    const fileInput = ref<HTMLInputElement | null>(null);
    const textarea = ref<HTMLTextAreaElement | null>(null);
    const { onCompositionStart, onCompositionEnd, isComposing } = useImeCompositionGuard();

    useTextareaAutoGrow({
        textarea,
        text: messageText,
        maxRows: 6,
        onBeforeResize: () => emit('before-resize'),
        onResize: () => emit('resize'),
    });

    const emitComposerResize = () => {
        emit('before-resize');
        nextTick(() => {
            emit('resize');
        });
    };

    watch(
        () => messageAttachments.value.length,
        () => {
            // Attachment change resizes the composer without firing auto-grow.
            emitComposerResize();
        }
    );

    watch(
        () => smsAiDraft.state.value === SmsAiDraftState.Loading,
        () => {
            // Textarea<->loading-box swap resizes without firing auto-grow.
            emitComposerResize();
        }
    );

    // Computed
    const isEmptyBody = computed<boolean>(
        () => !messageText.value.trim() && messageAttachments.value.length === 0
    );
    const hasMessageText = computed<boolean>(() => !!messageText.value.trim());
    const hasReachedSmsTextLimit = computed<boolean>(
        () => smsTextLength(messageText.value) >= SMS_TEXT_MAX_LENGTH
    );

    const isProcessing = computed<boolean>(() => isParsingTemplate.value || isSending.value);
    const isSendUnavailable = computed<boolean>(
        () =>
            isEmptyBody.value ||
            smsTextLength(messageText.value) > SMS_TEXT_MAX_LENGTH ||
            isProcessing.value ||
            props.disabled ||
            smsAiDraft.isInProgress.value
    );

    // Lock composer actions while generating (textarea is hidden).
    const areComposerActionsDisabled = computed<boolean>(
        () => isProcessing.value || props.disabled || smsAiDraft.isInProgress.value
    );

    const canGenerateSmsAiDraft = computed<boolean>(
        () => smsAiDraftEntitlement.canGenerate.value && !hasMessageText.value
    );
    const canUpdateSmsAiDraft = computed<boolean>(
        () => smsAiDraftEntitlement.canGenerate.value && hasMessageText.value
    );

    const resetSmsAiInstruction = () => {
        smsAiInstructionResetKey.value += 1;
    };

    const onMessageTextInput = () => {
        emit('write-intent');
    };

    const applySmsAiDraft = (draft: { text: string }): boolean => {
        if (
            props.disabled ||
            isProcessing.value ||
            smsTextLength(draft.text) > SMS_TEXT_MAX_LENGTH
        ) {
            return false;
        }

        messageText.value = draft.text;
        emit('write-intent');

        return true;
    };

    const generateSmsAiDraft = async () => {
        if (areComposerActionsDisabled.value || !canGenerateSmsAiDraft.value) {
            return;
        }

        await smsAiDraft.generate({
            locale: smsAiDraftEntitlement.locale.value,
            snapshot: { text: messageText.value },
            applyDraft: applySmsAiDraft,
        });
    };

    const updateSmsAiDraft = async ({ instruction }: { instruction: string }) => {
        if (areComposerActionsDisabled.value || !canUpdateSmsAiDraft.value) {
            return;
        }

        const textBeforeUpdate = messageText.value;

        await smsAiDraft.update({
            locale: smsAiDraftEntitlement.locale.value,
            snapshot: { text: textBeforeUpdate },
            text: textBeforeUpdate,
            instruction,
            applyDraft: applySmsAiDraft,
        });

        if (smsAiDraft.state.value === SmsAiDraftState.Inserted) {
            resetSmsAiInstruction();
        }
    };

    const stopSmsAiDraft = async () => {
        await smsAiDraft.stop((snapshot) => {
            messageText.value = snapshot.text;
        });
        resetSmsAiInstruction();
    };

    const undoSmsAiDraft = () => {
        smsAiDraft.undo((snapshot) => {
            messageText.value = snapshot.text;
            emit('write-intent');
        });
        resetSmsAiInstruction();
    };

    const resetComposer = () => {
        smsAiDraft.reset();
        resetSmsAiInstruction();
        messageText.value = '';
        revokeAllObjectUrls();
        messageAttachments.value = [];
        isEmojiPickerOpen.value = false;
        isParsingTemplate.value = false;
        isSending.value = false;
        hasSentWithPendingAttachments.value = false;
    };

    watch(
        [() => niotextStore.conversationSelectionGeneration, () => niotextStore.currentLeadId],
        () => {
            composerContextVersion.value++;
            resetComposer();
        }
    );

    watch(messageText, (nextText) => {
        const codePoints = [...nextText];

        if (codePoints.length > SMS_TEXT_MAX_LENGTH) {
            messageText.value = codePoints.slice(0, SMS_TEXT_MAX_LENGTH).join('');
        }
    });

    onBeforeUnmount(() => {
        composerContextVersion.value++;
        resetComposer();
    });

    const sendSmsButtonStyleClassName = computed(() => {
        return {
            'cursor-not-allowed text-blue-800/60': isSendUnavailable.value,
            'cursor-pointer text-blue-800/80 hover:text-blue-800/90': !isSendUnavailable.value,
        };
    });

    const sendAttachment = async (
        attachment: Attachment,
        contextIsCurrent: () => boolean
    ): Promise<boolean> => {
        try {
            const file = attachment.file;
            const responseVapor = await Vapor.store(file, {
                baseURL: axios.defaults.baseURL,
                headers: axios.defaults.headers.common,
            });

            if (!contextIsCurrent()) {
                return false;
            }

            await niotextStore.sendMessage({
                key: responseVapor.key,
                name: file.name,
                content_type: file.type,
            });

            return contextIsCurrent();
        } catch (error: any) {
            if (contextIsCurrent()) {
                const validationMessage = error?.response?.data?.errors?.content_type?.[0];
                notify.error(
                    validationMessage || t('smsCommunication.notifications.attachmentSendFailed')
                );
            }

            return false;
        }
    };

    const updateAttachmentDraftAfterSend = (
        attachments: Attachment[],
        sentAttachments: Set<Attachment>
    ): void => {
        if (sentAttachments.size === attachments.length && attachments.length > 0) {
            revokeAllObjectUrls();
            messageAttachments.value = [];
        } else if (sentAttachments.size > 0) {
            for (const attachment of sentAttachments) {
                revokeObjectUrl(attachment.preview);
            }

            messageAttachments.value = attachments.filter(
                (attachment) => !sentAttachments.has(attachment)
            );
        }
    };

    const sendMessage = async () => {
        if (isSendUnavailable.value) {
            return;
        }

        const contextVersion = composerContextVersion.value;
        const text = messageText.value;
        const attributionInteractionId = smsAiDraft.appliedInteractionId.value;
        const attachments = [...messageAttachments.value];
        const contextIsCurrent = (): boolean => composerContextVersion.value === contextVersion;

        try {
            isSending.value = true;
            let hasSentSuccessfully = false;
            const sentAttachments = new Set<Attachment>();

            if (text.trim()) {
                await niotextStore.sendMessage({
                    body: text,
                    ...(attributionInteractionId === null
                        ? {}
                        : { ai_interaction_id: attributionInteractionId }),
                });

                if (!contextIsCurrent()) {
                    return;
                }

                hasSentSuccessfully = true;
                smsAiDraft.clearGeneratedDraft();
            }

            await Promise.all(
                attachments.map(async (attachment) => {
                    if (await sendAttachment(attachment, contextIsCurrent)) {
                        hasSentSuccessfully = true;
                        sentAttachments.add(attachment);
                    }
                })
            );

            if (!contextIsCurrent()) {
                return;
            }

            if (!hasSentSuccessfully) {
                return;
            }

            if (!text.trim()) {
                smsAiDraft.clearGeneratedDraft();
            }

            if (text.trim()) {
                messageText.value = '';
                resetSmsAiInstruction();
            }

            updateAttachmentDraftAfterSend(attachments, sentAttachments);

            if (messageAttachments.value.length === 0) {
                hasSentWithPendingAttachments.value = false;
                emit('send-success');
            } else {
                hasSentWithPendingAttachments.value = true;
            }
        } catch {
            if (contextIsCurrent()) {
                notify.error(t('smsCommunication.notifications.smsSendFailed'));
            }
        } finally {
            if (contextIsCurrent()) {
                isSending.value = false;
            }
        }
    };

    const onEnterKey = (event: KeyboardEvent) => {
        if (event.shiftKey) {
            return;
        }

        if (isComposing(event)) {
            return;
        }

        event.preventDefault();

        sendMessage();
    };

    const onFileChange = (event: Event) => {
        const fileInput = event.target as HTMLInputElement;

        if (fileInput.files && fileInput.files.length > 0) {
            const existingKeys = new Set(
                messageAttachments.value.map((attachment) => getAttachmentKey(attachment.file))
            );

            let added = false;

            for (const element of fileInput.files) {
                const file = element;

                if (!SUPPORTED_ATTACHMENT_MIME_TYPES.includes(file.type)) {
                    notify.warning(
                        t('smsCommunication.notifications.unsupportedFileType', {
                            name: file.name,
                        })
                    );
                    continue;
                }

                const fileKey = getAttachmentKey(file);

                if (existingKeys.has(fileKey)) {
                    continue;
                }

                existingKeys.add(fileKey);

                messageAttachments.value.push({
                    file,
                    preview: createObjectUrl(file).url,
                });

                added = true;
            }

            if (added) {
                emit('write-intent');
            }
        }

        if (fileInput) {
            fileInput.value = '';
        }
    };

    const selectImage = () => {
        fileInput.value?.click();
    };

    const getAttachmentKey = (file: File): string => {
        return `${file.name}-${file.size}-${file.lastModified}`;
    };

    const removeAttachment = (index: number) => {
        const attachment = messageAttachments.value[index];

        if (!attachment) {
            return;
        }

        revokeObjectUrl(attachment.preview);
        messageAttachments.value.splice(index, 1);

        if (
            hasSentWithPendingAttachments.value &&
            messageAttachments.value.length === 0 &&
            !messageText.value.trim()
        ) {
            hasSentWithPendingAttachments.value = false;
            emit('send-success');
        }
    };

    const getPreferredTemplateLocale = (): SmsTemplateLocale | undefined => {
        const locale = niotextStore.currentLead?.locale;

        return locale === 'en' || locale === 'fr' ? locale : undefined;
    };

    const openSmsTemplateModal = () => {
        openModal({
            component: SmsTemplateModal,
            props: {
                onInsert: insertTemplate,
                preferredLocale: getPreferredTemplateLocale(),
            },
        });
    };

    const insertTemplate = async (templateId: number, locale: string) => {
        const leadId = niotextStore.currentLeadId;

        if (!leadId) {
            return;
        }

        const contextVersion = composerContextVersion.value;
        const contextIsCurrent = (): boolean => composerContextVersion.value === contextVersion;

        try {
            isParsingTemplate.value = true;

            const parsedTemplate = await niotextStore.parseTemplate(templateId, locale, leadId);

            if (!contextIsCurrent()) {
                return;
            }

            smsAiDraft.reset();
            messageText.value = parsedTemplate;

            emit('write-intent');
        } catch {
            if (contextIsCurrent()) {
                notify.error(t('smsCommunication.notifications.parseTemplateFailed'));
            }
        } finally {
            if (contextIsCurrent()) {
                isParsingTemplate.value = false;
            }
        }
    };

    const toggleEmojiPicker = () => {
        isEmojiPickerOpen.value = !isEmojiPickerOpen.value;
    };

    const closeEmojiPicker = () => {
        isEmojiPickerOpen.value = false;
    };

    const onEmojiSelect = (emoji: { i: string }) => {
        messageText.value += emoji.i;
        emit('write-intent');
    };
</script>
